<!-- app-subdomain-creators\src\Components\GlobalNotificationToast.vue -->

<template>
  <div
    aria-live="assertive"
    class="pointer-events-none fixed inset-0 z-[120] mt-12 flex items-end px-4 py-6 sm:items-start sm:p-6"
  >
    <div class="flex w-full flex-col items-center space-y-4 sm:items-end">
      <transition
        enter-active-class="transform ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-150"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showNotification"
          :class="[
            'pointer-events-auto w-full max-w-sm overflow-hidden rounded-xl shadow-lg ring-1 backdrop-blur-[3px]',
            variantStyles.bg,
            variantStyles.ring,
          ]"
        >
          <div class="p-4">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <div
                  v-if="notificationMessage.avatarUrl"
                  class="relative inline-flex"
                >
                  <img
                    :src="normalizeAvatarSrc(notificationMessage.avatarUrl)"
                    alt="Author avatar"
                    :class="isFlixTheatreReleaseToast || isBtcReceiveToast
                      ? 'h-8 w-auto max-w-[112px] object-contain'
                      : 'h-8 w-8 rounded-full object-cover ring-1 ring-white/60'"
                    @error="handleToastAvatarError"
                  >
                  <img
                    v-if="reactionIcon && !isFlixTheatreReleaseToast && !isBtcReceiveToast"
                    :src="reactionIcon"
                    alt="Reaction"
                    class="absolute -bottom-1 -right-1 h-4 w-4 rounded-full bg-white ring-1 ring-white"
                  >
                </div>
                <img
                  v-else-if="reactionIcon"
                  :src="reactionIcon"
                  alt="Reaction"
                  class="mt-1 h-4 w-4 rounded-full bg-white ring-1 ring-white"
                >
                <CheckCircleIcon
                  v-else-if="!isError"
                  :class="['h-6 w-6', variantStyles.icon]"
                  aria-hidden="true"
                />
                <ExclamationCircleIcon
                  v-else
                  :class="['h-6 w-6', variantStyles.icon]"
                  aria-hidden="true"
                />
              </div>
              <div class="ml-3 w-0 flex-1 pt-0.5">
                <p
                  v-if="!isFlixTheatreReleaseToast && notificationMessage.title"
                  :class="['text-sm font-semibold', variantStyles.title]"
                >
                  {{ notificationMessage.title }}
                </p>
                <p
                  v-if="notificationMessage.body"
                  :class="[
                    isFlixTheatreReleaseToast ? 'text-sm font-semibold' : 'mt-1 text-sm',
                    variantStyles.body,
                  ]"
                >
                  {{ notificationMessage.body }}
                </p>
                <router-link
                  v-if="notificationMessage.linkPath"
                  :to="notificationMessage.linkPath"
                  class="mt-2 inline-flex text-xs font-semibold text-[#2D8CFF] hover:underline"
                  @click="closeToast"
                >
                  {{ notificationMessage.linkLabel || 'View post' }}
                </router-link>
              </div>
              <div class="ml-4 flex flex-shrink-0">
                <button
                  type="button"
                  class="inline-flex rounded-md bg-transparent text-zinc-400 hover:text-zinc-600 focus:outline-none"
                  @click="closeToast"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance } from 'vue';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/vue/24/outline';
import { XMarkIcon } from '@heroicons/vue/20/solid';
const likeIcon = new URL('@/assets/media/images/newsfeed-like-icon.png', import.meta.url).href;
const loveIcon = new URL('@/assets/media/images/newsfeed-love-icon.png', import.meta.url).href;
const careIcon = new URL('@/assets/media/images/newsfeed-care-icon.png', import.meta.url).href;
const hahaIcon = new URL('@/assets/media/images/newsfeed-haha-icon.png', import.meta.url).href;
const wowIcon = new URL('@/assets/media/images/newsfeed-wow-icon.png', import.meta.url).href;
const sadIcon = new URL('@/assets/media/images/newsfeed-sad-icon.png', import.meta.url).href;
const angryIcon = new URL('@/assets/media/images/newsfeed-angry-icon.png', import.meta.url).href;
const commentIcon = new URL('@/assets/media/images/comment-platforma-newsfeed-icon.png', import.meta.url).href;
const postIcon = new URL('@/assets/media/images/post-platforma-newsfeed-icon.png', import.meta.url).href;
const defaultAvatarSrc = new URL('@/assets/media/images/default-avatar.png', import.meta.url).href;

const app = getCurrentInstance();
const apiProxyUrl = process.env.VUE_APP_API_PROXY_URL || '';
const showNotification = app?.appContext?.config?.globalProperties?.$showNotification;
const isError = app?.appContext?.config?.globalProperties?.$isError;
const notificationMessage =
  app?.appContext?.config?.globalProperties?.$notificationMessage;
const notificationVariant =
  app?.appContext?.config?.globalProperties?.$notificationVariant;

function isKnownDefaultAvatarPlaceholder(value) {
  const normalized = String(value || '').trim().toLowerCase();
  if (!normalized) return false;
  return (
    normalized === 'src/assets/media/images/default-avatar.png' ||
    normalized === '/src/assets/media/images/default-avatar.png' ||
    normalized === 'default-avatar.png' ||
    normalized.endsWith('/default-avatar.png') ||
    normalized.includes('/media/images/default-avatar.png')
  );
}

function normalizeAvatarSrc(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const lowered = raw.toLowerCase();
  if (lowered === 'null' || lowered === 'undefined') return defaultAvatarSrc;
  if (isKnownDefaultAvatarPlaceholder(raw)) return defaultAvatarSrc;

  const match = raw.match(/\/api\/image\/([0-9a-fA-F-]{8,})/);
  if (!match?.[1]) return raw;

  return `${apiProxyUrl}/api/creator-newsfeed/image/${match[1]}`;
}

function handleToastAvatarError(event) {
  const target = event?.target;
  if (!target || typeof target.src !== 'string') return;
  if (target.src !== defaultAvatarSrc) {
    target.src = defaultAvatarSrc;
  }
}

function closeToast() {
  if (showNotification) showNotification.value = false;
}

const reactionIcon = computed(() => {
  const type = String(notificationMessage?.value?.reactionType || '').toLowerCase().trim();
  if (type === 'like') return likeIcon;
  if (type === 'love') return loveIcon;
  if (type === 'care') return careIcon;
  if (type === 'haha') return hahaIcon;
  if (type === 'wow') return wowIcon;
  if (type === 'sad') return sadIcon;
  if (type === 'angry') return angryIcon;
  if (type === 'comment') return commentIcon;
  if (notificationMessage?.value?.postBadge) return postIcon;
  return '';
});

const isFlixTheatreReleaseToast = computed(() => {
  return String(notificationMessage?.value?.toastStyle || '').trim().toLowerCase() === 'flix-theatre-release';
});
const isBtcReceiveToast = computed(() => {
  return String(notificationMessage?.value?.toastStyle || '').trim().toLowerCase() === 'btc-receive';
});

const variantStyles = computed(() => {
  switch (notificationVariant?.value) {
    case 'error':
      return {
        bg: 'bg-red-50/95',
        ring: 'ring-red-200',
        icon: 'text-red-500',
        title: 'text-red-900',
        body: 'text-red-700',
      };
    case 'info':
      return {
        bg: 'bg-blue-50/95',
        ring: 'ring-blue-200',
        icon: 'text-blue-500',
        title: 'text-blue-900',
        body: 'text-blue-700',
      };
    default:
      return {
        bg: 'bg-black/50 bg-gradient-to-br from-emerald-500/20 via-black/50 to-emerald-900/35',
        ring: 'ring-1 ring-inset ring-green-500/20',
        icon: 'text-green-400',
        title: 'text-zinc-200',
        body: 'text-zinc-200',
      };
  }
});
</script>
